import React, { useCallback, useMemo } from 'react';

import { Query } from '@apollo/client/react/components';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import cx from 'classnames';

import { deviceFromGraphQL, GraphQLDevice } from 'client/app/api/deviceFromGraphql';
import { QUERY_ALL_DEVICES } from 'client/app/api/gql/queries';
import DeviceSelectorDialog from 'client/app/components/DeviceSelectorDialog';
import { AllDevicesQuery } from 'client/app/gql';
import {
  buildConfiguredDevice,
  configHasDeletedDevice,
  configHasDeletedDeviceRunConfig,
  getSelectedDevices,
} from 'client/app/lib/workflow/deviceConfigUtils';
import { ConfiguredDevice, ConfiguredDeviceDeviceId } from 'common/types/bundle';
import {
  addAccessibleDevice,
  removeAccessibleDeviceByDeviceId,
} from 'common/types/bundleConfigUtils';
import { DeviceRunConfig, SimpleDevice } from 'common/types/device';
import Colors from 'common/ui/Colors';
import Button from 'common/ui/components/Button';
import DeviceList from 'common/ui/components/DeviceList';
import GraphQLErrorPanel from 'common/ui/components/GraphQLErrorPanel';
import makeStylesHook from 'common/ui/hooks/makeStylesHook';
import useDialog from 'common/ui/hooks/useDialog';

type Props = {
  allDevices: readonly GraphQLDevice[];
  /** Devices that are already selected */
  configuredDevices: ConfiguredDevice[];
  isDisabled?: boolean;
  /**
   * Callback when user changes the selected devices.
   * This prop is optional. If you don't provide the callback, this view will be read-only
   * (no button to select devices).
   */
  onSelectedDevicesChange?: (deviceConfiguration: ConfiguredDevice[]) => void;
};

/**
 * @deprecated Device Selector selects devices works only for a single stage workflow. Does NOT support multi-stage workflows.
 * For an alternative, see DeviceSelectorCard component.
 */
function DeviceSelector(props: Props) {
  const classes = useStyles();
  const { allDevices, configuredDevices, onSelectedDevicesChange, isDisabled } = props;

  const [deviceSelectorDialog, openDeviceSelectorDialog] =
    useDialog(DeviceSelectorDialog);

  const selectedDevices = useMemo<GraphQLDevice[]>(() => {
    return getSelectedDevices(allDevices, configuredDevices);
  }, [allDevices, configuredDevices]);

  // We still need to convert to the datatype shared with AnthaHub to render the device list
  const selectedDevicesCommon = useMemo(
    () => selectedDevices.map(deviceFromGraphQL),
    [selectedDevices],
  );

  const usingMissingSelectedDevice = useMemo(
    () => configHasDeletedDevice(selectedDevices, configuredDevices),
    [configuredDevices, selectedDevices],
  );
  const usingMissingDeviceConfig = useMemo(
    () => configHasDeletedDeviceRunConfig(selectedDevices, configuredDevices),
    [configuredDevices, selectedDevices],
  );

  const handleRemoveDevice = useCallback(
    (deviceToRemove: SimpleDevice) => {
      const accessibleDevices = selectedDevices.find(
        device => device.id === deviceToRemove.id,
      )?.accessibleDevices;

      const idsToRemove = [
        deviceToRemove.id,
        ...(accessibleDevices || []).map(ad => ad.id),
      ];
      const newConfiguredDevices = configuredDevices.filter(
        // multiple configured device with the same deviceId can exist if the same
        // device is re-used across stages. However, the UI code here doesn't support
        // multi-stage workflow and assumes that the device id uniquely identifies
        // the configured device. This is OK for single stage workflows, but will
        // need addressing if we ever support multi stage here.
        cd => !idsToRemove.includes(cd.deviceId),
      );

      onSelectedDevicesChange?.(newConfiguredDevices);
    },
    [configuredDevices, onSelectedDevicesChange, selectedDevices],
  );

  const handleSelectDevices = useCallback(async () => {
    const result = await openDeviceSelectorDialog({
      preselectedDevices: selectedDevices,
      preSelectedDeviceConfigs: configuredDevices.reduce((acc, cd) => {
        acc[cd.deviceId] = cd.runConfigId;
        return acc;
      }, {} as Record<string, string | undefined>),
    });

    if (!result) {
      // User canceled
      return;
    }

    const newSelection = buildConfiguredDevice(
      result.selectedDevices,
      result.selectedConfigs,
    ).configuredDevices;
    onSelectedDevicesChange?.(newSelection);
  }, [
    openDeviceSelectorDialog,
    selectedDevices,
    configuredDevices,
    onSelectedDevicesChange,
  ]);

  const handleAccessibleDeviceEnabledChange = useCallback(
    (accessibleDevice: SimpleDevice, isEnabled: boolean) => {
      if (!isEnabled) {
        const deviceSelectionMinusAccessibleDevice = removeAccessibleDeviceByDeviceId(
          configuredDevices,
          accessibleDevice.id as ConfiguredDeviceDeviceId,
        );
        onSelectedDevicesChange?.(deviceSelectionMinusAccessibleDevice);
      } else {
        const parentDevice = selectedDevicesCommon.find(device =>
          device.accessibleDevices.some(a => a.id === accessibleDevice.id),
        );
        if (!parentDevice) {
          console.error(
            `Trying to enable an accessible device ${accessibleDevice.id} ` +
              'but there is no corresponding parent device in the workflow config. ' +
              'This can only happen if the workflow config is in a bad state due to a bug.',
          );
          return;
        }
        const newConfiguredDevices = addAccessibleDevice(
          configuredDevices,
          accessibleDevice,
          parentDevice.id,
        );
        onSelectedDevicesChange?.(newConfiguredDevices);
      }
    },
    [configuredDevices, onSelectedDevicesChange, selectedDevicesCommon],
  );

  const handleSelectConfig = useCallback(
    (deviceId: string) => (newRunConfig?: DeviceRunConfig) => {
      // Prevent users from selecting the default message ("Please select a config")
      if (!newRunConfig) {
        return;
      }
      const newConfiguredDevices = configuredDevices.map(cd => {
        if (cd.deviceId === deviceId) {
          return {
            ...cd,
            runConfigId: newRunConfig.id,
            // we don't have access to the version here...
          };
        }
        return cd;
      });

      onSelectedDevicesChange?.(newConfiguredDevices);
    },
    [configuredDevices, onSelectedDevicesChange],
  );

  const isEditable = !isDisabled && !!onSelectedDevicesChange;
  const noDevicesSelected = selectedDevices.length === 0;

  return (
    <>
      {noDevicesSelected && !usingMissingSelectedDevice && (
        <Typography
          variant="body1"
          color="textSecondary"
          className={cx(classes.message, classes.emptyListText)}
        >
          No execution mode selected
        </Typography>
      )}
      {usingMissingSelectedDevice && (
        <Typography
          variant="body1"
          color="error"
          className={cx(classes.message, classes.problem)}
        >
          The original device for this form has been deleted. Please select a different
          execution mode.
        </Typography>
      )}
      {usingMissingDeviceConfig && (
        <Typography
          variant="body1"
          color="error"
          className={cx(classes.message, classes.problem)}
        >
          The config of the device has been deleted. Please select a different config for
          the device.
        </Typography>
      )}
      {!noDevicesSelected && (
        <DeviceList
          devices={selectedDevicesCommon}
          configuredDevices={configuredDevices}
          isEditable={isEditable}
          onAccessibleDeviceEnabledChange={
            (isEditable && handleAccessibleDeviceEnabledChange) || undefined
          }
          onRemoveDevice={(isEditable && handleRemoveDevice) || undefined}
          onSelectConfig={(isEditable && handleSelectConfig) || undefined}
        />
      )}
      {onSelectedDevicesChange && !isDisabled && (
        <Button
          variant="secondary"
          onClick={handleSelectDevices}
          className={classes.selectDeviceBtn}
        >
          Select execution mode
        </Button>
      )}
      {deviceSelectorDialog}
    </>
  );
}

const useStyles = makeStylesHook({
  message: {
    margin: '0 24px',
    fontStyle: 'italic',
    marginTop: '1rem',
  },
  emptyListText: {
    color: Colors.GREY_60,
  },
  problem: {
    color: Colors.ERROR,
  },
  selectDeviceBtn: {
    marginLeft: '1rem',
    marginTop: '1rem',
  },
});

/**
 * @deprecated DeviceSelectorContainer selects devices works only for a single stage workflow. Does NOT support multi-stage workflows.
 * For an alternative, see DeviceSelectorCard component.
 *
 * This wrapper adds device config data sourced from graphql to DeviceSelector.
 * This is easier than adding the same piece of code in all places where the component is used.
 * When we start fetching device data from appserver, the list of configs will just be
 * a field on the device object, but for now we need to augment the data we get from microservices.
 */
export default function DeviceSelectorContainer(props: Omit<Props, 'allDevices'>) {
  return (
    <Query<AllDevicesQuery> query={QUERY_ALL_DEVICES}>
      {({ data, loading, error, refetch }) => {
        if (loading) {
          return (
            <div style={{ margin: '1rem' }}>
              <CircularProgress />
            </div>
          );
        } else if (error) {
          return <GraphQLErrorPanel error={error} onRetry={refetch} />;
        } else if (data) {
          return <DeviceSelector allDevices={data.devices} {...props} />;
        }
        return null;
      }}
    </Query>
  );
}
